// services/ChartImageService.ts

import React, { createElement } from "react"
import { createRoot } from "react-dom/client"
import { toPng } from "html-to-image"
import { AxiosResponse } from "axios"

// Chart components - heavy imports isolated here
import BarChart from "shared/ui/Charts/BarChart.component"
import PieChart from "shared/ui/Charts/PieChart.component"
import LineChart from "shared/ui/Charts/LineChart.component"

// Import types from their proper locations
import {
  STATISTIC_TYPE,
  ELEMENT_TYPE,
  ChartElementSettings,
  PieChartElementSettings,
  TimeSeriesChartSettings,
  TableElementSettings,
  ChartRenderDataPayload,
  DataElementConfig,
  LEGEND_POSITION,
} from "types/ReportingDashboard/elements.types"
import { ChartElementWithConfig } from "types/ReportingDashboard/reporting.types"

import { QueryInstance } from "types/engine.types"
import { EngineQueryResult, RTableContent } from "types/engineOutput.types"
import { TABLE_OPERATION_TYPE } from "types/ReportingDashboard/elements.types"

// Import conversion utilities and their types
import {
  convertDataToTable,
  TransformedTableData,
  PieChartStatisticGroup,
} from "utils/convertDataToTable"

import { convertEngineOutputToTimeSeries } from "utils/convertEngineOutputToTimeSeries"

import type {
  NivoLineChartSeries,
  TimeSeriesRawDataPayload,
} from "types/ReportingDashboard/timeseries.types"

// Define types for the service using proper enums
type ChartElementType =
  | ELEMENT_TYPE.CHART
  | ELEMENT_TYPE.PIE_CHART
  | ELEMENT_TYPE.TIME_SERIES_CHART

// Cache for converted data to prevent re-processing
const conversionCache = new WeakMap<RTableContent, TransformedTableData>()

/**
 * Get cached conversion or convert and cache
 */
const getCachedConversion = (
  data: RTableContent
): TransformedTableData | null => {
  if (!data) return null

  if (conversionCache.has(data)) {
    return conversionCache.get(data) || null
  }

  const converted = convertDataToTable(data)
  conversionCache.set(data, converted)
  return converted
}

/**
 * Generate script for different element types using existing logic from components
 */
export const generateScriptForElement = (
  elementType: ChartElementType | ELEMENT_TYPE.TABLE,
  config: DataElementConfig,
  filter: string
): string | null => {
  const timestamp = Date.now()
  const uniqueTableId = `export_tbl_${timestamp}`
  const filterCmd = filter ? ` UNIVERSE: <<${filter}>>` : ""

  switch (elementType) {
    case ELEMENT_TYPE.CHART: {
      const chartConfig = config as ChartElementSettings
      if (!chartConfig.selectedVariable || !chartConfig.breakdownVariable)
        return null

      // Use the configured table operation type (DTAB or XTAB)
      const operationType =
        chartConfig.tableOperationType || TABLE_OPERATION_TYPE.XTAB

      return `${operationType} ${uniqueTableId} <<${
        chartConfig.selectedVariable
      }>> <<${chartConfig.breakdownVariable}>>${filterCmd} OPERATORS: "${
        chartConfig.selectedStatisticType || STATISTIC_TYPE.N
      }"\nOUTPUT ${uniqueTableId}`
    }

    case ELEMENT_TYPE.PIE_CHART: {
      const pieConfig = config as PieChartElementSettings
      if (!pieConfig.selectedVariable) return null
      const tempTableId = `Temp_${timestamp}`
      return `QTABLE ${tempTableId}\nXTAB ${uniqueTableId} <<${
        pieConfig.selectedVariable
      }>> ${tempTableId}${filterCmd} OPERATORS: "${
        pieConfig.selectedStatisticType || STATISTIC_TYPE.N
      }"\nOUTPUT ${uniqueTableId}\nREMOVE ${tempTableId}`
    }

    case ELEMENT_TYPE.TIME_SERIES_CHART: {
      const timeConfig = config as TimeSeriesChartSettings
      if (
        !timeConfig.selectedPrimaryVariable ||
        !timeConfig.selectedBreakdownVariable ||
        !timeConfig.selectedWaveVariable
      )
        return null

      // Use the configured table operation type (DTAB or XTAB)
      const operationType =
        timeConfig.tableOperationType || TABLE_OPERATION_TYPE.XTAB
      const universeFilter = `UNIVERSE: <<${timeConfig.selectedWaveVariable}>>`
      const operatorsString = `OPERATORS: "${
        timeConfig.selectedStatisticType || STATISTIC_TYPE.N
      }"`

      return `${operationType} ${uniqueTableId} <<${timeConfig.selectedPrimaryVariable}>> <<${timeConfig.selectedBreakdownVariable}>> ${universeFilter} ${operatorsString}\nOUTPUT ${uniqueTableId}`
    }

    case ELEMENT_TYPE.TABLE: {
      const tableConfig = config as TableElementSettings
      if (
        !tableConfig.crossTabVariables ||
        tableConfig.crossTabVariables.length !== 2
      )
        return null
      const operatorsString = (
        tableConfig.selectedOperators || [
          STATISTIC_TYPE.N,
          STATISTIC_TYPE.BIG_N,
          STATISTIC_TYPE.ROW_PERCENT,
          STATISTIC_TYPE.COL_PERCENT,
          STATISTIC_TYPE.INDEX,
          STATISTIC_TYPE.PERC_PERCENT,
          STATISTIC_TYPE.AVG,
        ]
      )
        .map((op) => `"${op}"`)
        .join(",")
      return `XTAB ${uniqueTableId} <<${tableConfig.crossTabVariables[0]}>> <<${
        tableConfig.crossTabVariables[1]
      }>${filterCmd} OPERATORS: ${operatorsString}\n${
        tableConfig.isTransposed
          ? `OUTPUT ${uniqueTableId} /T`
          : `OUTPUT ${uniqueTableId}`
      }`
    }

    default:
      return null
  }
}

/**
 * Render chart component based on element type and data
 */
export const renderChartComponent = (
  elementType: ChartElementType,
  data: ChartRenderDataPayload | NivoLineChartSeries[],
  config: DataElementConfig,
  height: number = 400,
  width: number = 600
): React.ReactElement | null => {
  switch (elementType) {
    case ELEMENT_TYPE.CHART: {
      const chartData = data as ChartRenderDataPayload
      if (!chartData?.content) return null
      const converted = getCachedConversion(chartData.content)
      if (!converted) return null

      const { barChartData } = converted
      if (!barChartData) return null

      const chartConfig = config as ChartElementSettings

      return createElement(BarChart, {
        isChart: true,
        data: barChartData.data,
        keys: barChartData.keys,
        statisticTypes: [chartConfig.selectedStatisticType || STATISTIC_TYPE.N],
        xAxisLabel:
          chartConfig.selectedVariable?.split("//").pop() || "Primary Variable",
        isDashboard: true,
        dashboardSelectedKeys:
          chartConfig.dashboardSelectedKeys ||
          barChartData.keys.filter((k) => k !== "Totals"),
        dashboardSelectedCategories:
          chartConfig.dashboardSelectedCategories ||
          barChartData.data
            .filter((i) => i.xValue !== "Totals")
            .map((i) => String(i.xValue)),
        layout:
          chartConfig.chartType === "horizontal" ? "horizontal" : "vertical",
        showTotals: chartConfig.showTotals || false,
        setShowTotals: () => {}, // No-op for image generation
        height,
        width, // Pass width for image generation sizing
      })
    }

    case ELEMENT_TYPE.PIE_CHART: {
      const chartData = data as ChartRenderDataPayload
      if (!chartData?.content) return null
      const converted = getCachedConversion(chartData.content)
      if (!converted || !converted.pieChartData) return null

      const pieConfig = config as PieChartElementSettings
      const td = converted
      let pieData: PieChartStatisticGroup[] = td.pieChartData.categoryData || []

      if (
        pieConfig.dashboardSelectedCategories &&
        pieConfig.dashboardSelectedCategories.length > 0
      ) {
        pieData = pieData
          .map((statGroup) => ({
            ...statGroup,
            data: (statGroup.data || []).filter(
              (item) =>
                pieConfig.dashboardSelectedCategories!.includes(
                  String(item.id)
                ) || item.id === "Totals"
            ),
          }))
          .filter((statGroup) => statGroup.data && statGroup.data.length > 0)
      }

      return createElement(PieChart, {
        data: pieData,
        isChart: true,
        selectedStatistic: pieConfig.selectedStatisticType || STATISTIC_TYPE.N,
        legendPosition: pieConfig.legendPosition || LEGEND_POSITION.RIGHT,
        hideArcLabels: pieConfig.hideArcLabels || false,
      })
    }

    case ELEMENT_TYPE.TIME_SERIES_CHART: {
      const timeData = data as NivoLineChartSeries[]
      if (!Array.isArray(timeData)) return null

      const timeConfig = config as TimeSeriesChartSettings

      // Apply dashboard filtering like other chart types
      const filteredData = timeData
        .filter((series) =>
          timeConfig.selectedDashboardSeries &&
          timeConfig.selectedDashboardSeries.length > 0
            ? timeConfig.selectedDashboardSeries.includes(series.id)
            : true
        )
        .map((series) => ({
          ...series,
          data: series.data.filter((point) =>
            timeConfig.selectedDashboardWaves &&
            timeConfig.selectedDashboardWaves.length > 0
              ? timeConfig.selectedDashboardWaves.includes(point.x)
              : true
          ),
        }))

      const formatYTick = (value: unknown): string => {
        const statType = timeConfig.selectedStatisticType || STATISTIC_TYPE.N
        if (
          String(statType).includes("%") ||
          String(statType).includes("perc")
        ) {
          const num = parseFloat(String(value))
          return isNaN(num) ? String(value) : `${(num * 100).toFixed(1)}%`
        }
        const num = parseFloat(String(value))
        return isNaN(num)
          ? String(value)
          : Number.isInteger(num)
          ? num.toLocaleString()
          : num.toFixed(2)
      }

      return createElement(LineChart, {
        data: filteredData,
        xAxisLabel:
          timeConfig.selectedWaveVariable?.split("//").pop() || "Waves",
        yAxisLabel: String(
          timeConfig.selectedStatisticType || STATISTIC_TYPE.N
        ),
        height: height, // Use the passed height parameter consistently
        formatYTick,
      })
    }

    default:
      return null
  }
}

/**
 * Generate chart image from existing data
 */
export const generateChartImage = async (
  elementType: ChartElementType,
  data: ChartRenderDataPayload | NivoLineChartSeries[],
  config: DataElementConfig
): Promise<string | null> => {
  // Only process chart elements
  if (
    ![
      ELEMENT_TYPE.CHART,
      ELEMENT_TYPE.PIE_CHART,
      ELEMENT_TYPE.TIME_SERIES_CHART,
    ].includes(elementType)
  ) {
    return null
  }

  // Use actual element dimensions from config
  const width = config.width || 600
  const height = config.height || 400

  // Create temporary container for rendering charts off-screen
  const tempContainer = document.createElement("div")
  tempContainer.style.position = "absolute"
  tempContainer.style.top = "-9999px"
  tempContainer.style.left = "-9999px"
  tempContainer.style.width = `${width}px`
  tempContainer.style.height = `${height}px`
  tempContainer.style.backgroundColor = "#ffffff"
  tempContainer.style.padding = "0"
  tempContainer.style.margin = "0"
  tempContainer.style.overflow = "hidden"
  document.body.appendChild(tempContainer)

  try {
    // Create chart container with exact dimensions
    const chartContainer = document.createElement("div")
    chartContainer.style.width = `${width}px`
    chartContainer.style.height = `${height}px`
    chartContainer.style.backgroundColor = "#ffffff"
    chartContainer.style.padding = "0"
    chartContainer.style.margin = "0"
    chartContainer.style.boxSizing = "border-box"
    chartContainer.style.overflow = "hidden"
    tempContainer.appendChild(chartContainer)

    // Render chart using full container dimensions
    const root = createRoot(chartContainer)
    const chartComponent = renderChartComponent(
      elementType,
      data,
      config,
      height, // Pass the actual height to renderChartComponent
      width // Pass the actual width to renderChartComponent
    )

    if (!chartComponent) {
      console.warn(`Could not render chart component for type ${elementType}`)
      return null
    }

    // Render and wait for completion - more time for responsive charts
    await new Promise<void>((resolve) => {
      root.render(chartComponent)

      // Force a resize event to ensure ResponsiveBar detects container size
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"))
        // Additional time after resize event
        setTimeout(resolve, 800)
      }, 1200)
    })

    // Generate image with HD settings
    const imageDataUri = await toPng(chartContainer, {
      quality: 1.0,
      pixelRatio: 2.5, // High quality without scaling containers
      backgroundColor: "#ffffff",
      width: width,
      height: height,
      cacheBust: true,
    })

    // Clean up
    root.unmount()

    return imageDataUri
  } catch (error) {
    console.error(
      `Failed to generate image for chart type ${elementType}:`,
      error
    )
    return null
  } finally {
    // Clean up temporary container
    document.body.removeChild(tempContainer)
  }
}

/**
 * Fetch data and generate image for chart element (used during export)
 */
export const fetchDataAndGenerateImage = async (
  elementType: ChartElementType,
  config: DataElementConfig,
  RunEngineQuery: (
    instanceId: QueryInstance,
    script: string,
    isAsync: boolean
  ) => Promise<AxiosResponse<unknown>>,
  queryInstance: QueryInstance,
  filter: string
): Promise<string | null> => {
  // Only process chart elements
  if (
    ![
      ELEMENT_TYPE.CHART,
      ELEMENT_TYPE.PIE_CHART,
      ELEMENT_TYPE.TIME_SERIES_CHART,
    ].includes(elementType)
  ) {
    return null
  }

  try {
    // Generate script using existing element logic
    const script = generateScriptForElement(elementType, config, filter)
    if (!script) {
      console.warn(`Could not generate script for element type ${elementType}`)
      return null
    }

    // Execute script to get fresh data
    const result = await RunEngineQuery(queryInstance, script, false)
    const engineResult = result.data as EngineQueryResult

    if (result?.status !== 200 || !engineResult?.result?.output?.[0]?.content) {
      console.error(`Failed to get data for chart type ${elementType}`)
      return null
    }

    // Get the data in the format expected by each chart type
    let chartData: ChartRenderDataPayload | NivoLineChartSeries[]
    if (elementType === ELEMENT_TYPE.TIME_SERIES_CHART) {
      // For time series, we need to convert the raw engine output
      const timeConfig = config as TimeSeriesChartSettings
      const rawData = engineResult.result.output as TimeSeriesRawDataPayload

      const { lineChartNivoData } = convertEngineOutputToTimeSeries(
        rawData,
        timeConfig.selectedStatisticType || STATISTIC_TYPE.N,
        timeConfig.selectedPrimaryVariable,
        timeConfig.selectedBreakdownVariable
      )
      chartData = lineChartNivoData
    } else {
      // For other charts, use the ChartRenderDataPayload format
      chartData = {
        content: engineResult.result.output[0].content as RTableContent,
      }
    }

    // Generate image from the fresh data with actual dimensions
    return await generateChartImage(elementType, chartData, config)
  } catch (error) {
    console.error(
      `Failed to fetch data and generate image for chart type ${elementType}:`,
      error
    )
    return null
  }
}

/**
 * Batch generate images for multiple chart elements (used during export)
 */
export const batchGenerateImages = async (
  elementsWithConfigs: ChartElementWithConfig[],
  RunEngineQuery: (
    instanceId: QueryInstance,
    script: string,
    isAsync: boolean
  ) => Promise<AxiosResponse<unknown>>,
  queryInstance: QueryInstance,
  filter: string
): Promise<Record<string, string>> => {
  const generatedImages: Record<string, string> = {}

  const chartElements = elementsWithConfigs.filter((el) =>
    [
      ELEMENT_TYPE.CHART,
      ELEMENT_TYPE.PIE_CHART,
      ELEMENT_TYPE.TIME_SERIES_CHART,
    ].includes(el.elementType)
  )

  for (const { elementId, elementType, config } of chartElements) {
    try {
      const imageDataUri = await fetchDataAndGenerateImage(
        elementType,
        config,
        RunEngineQuery,
        queryInstance,
        filter
      )

      if (imageDataUri) {
        generatedImages[elementId] = imageDataUri
      } else {
        console.warn(`Failed to generate image for chart ${elementId}`)
      }
    } catch (error) {
      console.error(`Exception generating image for chart ${elementId}:`, error)
    }
  }

  return generatedImages
}

/**
 * Generate chart image from already processed data (used by chart elements)
 */
export const generateChartImageFromProcessedData = async (
  elementType: ChartElementType,
  processedData: ChartRenderDataPayload | NivoLineChartSeries[],
  config: DataElementConfig
): Promise<string | null> => {
  // Only process chart elements
  if (
    ![
      ELEMENT_TYPE.CHART,
      ELEMENT_TYPE.PIE_CHART,
      ELEMENT_TYPE.TIME_SERIES_CHART,
    ].includes(elementType)
  ) {
    return null
  }

  // Use actual element dimensions from config consistently
  const width = config.width || 600
  const height = config.height || 400

  // Create temporary container for rendering charts off-screen
  const tempContainer = document.createElement("div")
  tempContainer.style.position = "absolute"
  tempContainer.style.top = "-9999px"
  tempContainer.style.left = "-9999px"
  tempContainer.style.width = `${width}px`
  tempContainer.style.height = `${height}px`
  tempContainer.style.backgroundColor = "#ffffff"
  tempContainer.style.padding = "0"
  tempContainer.style.margin = "0"
  tempContainer.style.overflow = "hidden"
  document.body.appendChild(tempContainer)

  try {
    // Create chart container with proper dimensions and consistent padding
    const chartContainer = document.createElement("div")
    chartContainer.style.width = `${width}px`
    chartContainer.style.height = `${height}px`
    chartContainer.style.backgroundColor = "#ffffff"
    chartContainer.style.padding = "10px" // Consistent padding for all chart types
    chartContainer.style.boxSizing = "border-box"
    chartContainer.style.overflow = "hidden"
    tempContainer.appendChild(chartContainer)

    // Calculate inner dimensions accounting for padding consistently
    const innerHeight = height - 20 // 10px padding on top and bottom
    const innerWidth = width - 20 // 10px padding on left and right

    // Use renderChartComponent consistently for all chart types
    const root = createRoot(chartContainer)
    const chartComponent = renderChartComponent(
      elementType,
      processedData,
      config,
      innerHeight, // Pass consistent inner height to all chart types
      innerWidth // Pass consistent inner width to all chart types
    )

    if (!chartComponent) {
      console.warn(`Could not render chart component for type ${elementType}`)
      return null
    }

    // Render and wait for completion with resize trigger
    await new Promise<void>((resolve) => {
      root.render(chartComponent)

      // Force a resize event to ensure ResponsiveBar detects container size
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"))
        // Additional time after resize event
        setTimeout(resolve, 800)
      }, 700)
    })

    // Generate image with HD settings
    const imageDataUri = await toPng(chartContainer, {
      quality: 1.0,
      pixelRatio: 2.5, // HD quality
      backgroundColor: "#ffffff",
      width: width,
      height: height,
      cacheBust: true,
    })

    // Clean up
    root.unmount()

    return imageDataUri
  } catch (error) {
    console.error(
      `Failed to generate image for chart type ${elementType}:`,
      error
    )
    return null
  } finally {
    // Clean up temporary container
    document.body.removeChild(tempContainer)
  }
}
